import { useCallback, useEffect, useState } from 'react';
import { ArticlePage } from './ArticlePage';
import { BrandMark } from './BrandMark';
import { ReadingProgress, SearchDialog, SiteFooter, SiteHeader } from './components';
import { canonicalRouteFor, loadPageById, pageForRoute, pages, translationsFor } from './data';
import { HomePage } from './HomePage';
import { ArrowIcon, SearchIcon } from './icons';
import { CollectionPage, ComparisonPage, ExplorerPage } from './KnowledgeViews';
import { absoluteAssetUrl, headerImageFor, seoFor } from './page-presentation';
import { Link, NavigationProvider, useNavigation } from './router';
import type { SiteLanguage, SitePage, SitePageSummary } from './types';
import './styles.css';
import './page-imagery.css';

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLElement>(selector);
  if (!element) {
    element = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
    document.head.appendChild(element);
  }
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
}

function updateDocumentMetadata(page: SitePageSummary | null) {
  const seo = seoFor(page);
  const title = seo.title;
  const description = seo.description;
  const canonical = page ? `https://bottomsurgery.info${canonicalRouteFor(page)}` : 'https://bottomsurgery.info/';
  const socialImage = seo.image ?? (page ? headerImageFor(page) : null);
  const image = socialImage ? absoluteAssetUrl(socialImage.src) : 'https://bottomsurgery.info/seo/home.webp';
  const imageAlt = socialImage?.alt ?? 'A luminous violet and magenta lotus on a dark field.';
  const imageType = socialImage?.src.toLocaleLowerCase().endsWith('.png') ? 'image/png' : 'image/webp';
  document.title = title;
  document.documentElement.lang = page?.lang ?? 'en';
  document.documentElement.dir = page?.dir ?? 'ltr';
  upsertMeta('meta[name="description"]', { name: 'description', content: description });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: page?.kind === 'home' ? 'website' : 'article' });
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'bottomsurgery.info' });
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: page?.lang === 'es' ? 'es_ES' : 'en_US' });
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
  upsertMeta('meta[property="og:image:secure_url"]', { property: 'og:image:secure_url', content: image });
  upsertMeta('meta[property="og:image:type"]', { property: 'og:image:type', content: imageType });
  upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: String(socialImage?.width ?? 1200) });
  upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: String(socialImage?.height ?? 630) });
  upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: imageAlt });
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
  upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: imageAlt });
  upsertMeta('link[rel="canonical"]', { rel: 'canonical', href: canonical });
  upsertMeta('meta[name="robots"]', { name: 'robots', content: page ? 'index, follow' : 'noindex, follow' });
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
  if (page) {
    for (const alternate of translationsFor(page)) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = alternate.lang;
      link.href = `https://bottomsurgery.info${canonicalRouteFor(alternate)}`;
      document.head.appendChild(link);
    }
    const english = translationsFor(page).find((alternate) => alternate.lang === 'en');
    if (english) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = 'x-default';
      link.href = `https://bottomsurgery.info${canonicalRouteFor(english)}`;
      document.head.appendChild(link);
    }
    const alternateLocale = page.lang === 'es' ? 'en_US' : 'es_ES';
    upsertMeta('meta[property="og:locale:alternate"]', { property: 'og:locale:alternate', content: alternateLocale });
  }
}

function RouteLoading({ language }: { language: SiteLanguage }) {
  return (
    <main id="main-content" className="route-loading shell" aria-live="polite" aria-busy="true">
      <BrandMark className="route-loading__mark" title="bottomsurgery.info" />
      <p>{language === 'es' ? 'Cargando la guía…' : 'Loading the guide…'}</p>
    </main>
  );
}

function ContentError({ language, route }: { language: SiteLanguage; route: string }) {
  return (
    <main id="main-content" className="route-loading route-loading--error shell" role="alert">
      <span className="route-loading__mark">[!]</span>
      <p>{language === 'es' ? 'No se pudo cargar esta guía.' : 'This guide could not be loaded.'}</p>
      <a className="button button--primary" href={route}>{language === 'es' ? 'Intentar de nuevo' : 'Try again'}</a>
    </main>
  );
}

function NotFound({ language, onSearch }: { language: SiteLanguage; onSearch: () => void }) {
  const spanish = language === 'es';
  return (
    <main id="main-content" className="not-found shell">
      <span>404</span>
      <h1>{spanish ? 'Esta ruta no lleva a una página.' : 'This path does not lead to a page.'}</h1>
      <p>{spanish ? 'La URL puede haber cambiado. Busca en la base de conocimiento o vuelve al inicio.' : 'The URL may have changed, or the page may never have existed. Search the knowledge base or return to the beginning.'}</p>
      <div className="button-row">
        <button className="button button--primary" type="button" onClick={onSearch}><SearchIcon />{spanish ? 'Buscar' : 'Search the site'}</button>
        <Link className="button button--text" href={spanish ? '/es' : '/'}>{spanish ? 'Volver al inicio' : 'Go home'}<ArrowIcon /></Link>
      </div>
    </main>
  );
}

function AppContent({ initialPage }: { initialPage: SitePage | null }) {
  const { path } = useNavigation();
  const summary = pageForRoute(path);
  const language = summary?.lang ?? (path === '/es' || path.startsWith('/es/') ? 'es' : 'en');
  const designedHome = Boolean(summary?.kind === 'home' && (summary.lang === 'en' || summary.lang === 'es'));
  const [loadedPage, setLoadedPage] = useState<SitePage | null>(() => initialPage);
  const [loading, setLoading] = useState(() => Boolean(summary && !designedHome && initialPage?.id !== summary.id));
  const [loadError, setLoadError] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => updateDocumentMetadata(summary), [summary]);

  useEffect(() => {
    if (!summary || designedHome) {
      setLoading(false);
      setLoadError(false);
      return;
    }
    if (loadedPage?.id === summary.id) {
      setLoading(false);
      setLoadError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    loadPageById(summary.id)
      .then((page) => {
        if (!cancelled) {
          setLoadedPage(page);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadedPage(null);
          setLoading(false);
          setLoadError(true);
        }
      });
    return () => { cancelled = true; };
  }, [designedHome, loadedPage?.id, summary]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      const typing = target instanceof Element && target.matches('input, textarea, select, [contenteditable="true"]');
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      } else if (!typing && event.key === '/') {
        event.preventDefault();
        openSearch();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openSearch]);

  let content;
  if (!summary) content = <NotFound language={language} onSearch={openSearch} />;
  else if (designedHome) content = <HomePage language={summary.lang} pages={pages} searchOpen={searchOpen} onSearch={openSearch} />;
  else if (loadError) content = <ContentError language={summary.lang} route={summary.route} />;
  else if (loading || loadedPage?.id !== summary.id) content = <RouteLoading language={summary.lang} />;
  else if (loadedPage.kind === 'collection') content = <CollectionPage page={loadedPage} pages={pages} />;
  else if (loadedPage.kind === 'explorer') content = <ExplorerPage page={loadedPage} pages={pages} />;
  else if (loadedPage.kind === 'comparison') content = <ComparisonPage page={loadedPage} pages={pages} />;
  else content = <ArticlePage key={loadedPage.id} page={loadedPage} pages={pages} />;

  return (
    <>
      <Link className="skip-link" href="#main-content">{language === 'es' ? 'Saltar al contenido' : 'Skip to content'}</Link>
      {summary && summary.wordCount > 0 && <ReadingProgress />}
      <SiteHeader page={summary} searchOpen={searchOpen} onSearch={openSearch} />
      {content}
      <SiteFooter language={language} />
      <SearchDialog open={searchOpen} language={language} onClose={closeSearch} />
    </>
  );
}

export function App({ initialPath = '/', initialPage = null }: { initialPath?: string; initialPage?: SitePage | null }) {
  return <NavigationProvider initialPath={initialPath}><AppContent initialPage={initialPage} /></NavigationProvider>;
}
